import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getUserDetails,
  followUser,
  unfollowUser,
} from "../../actions/userActions";
import { createChat, resetChatCreate } from "../../actions/chatActions";
import Loader from "../../components/Loader";
import {
  USER_DETAILS_RESET,
  USER_FOLLOW_RESET,
  USER_UNFOLLOW_RESET,
} from "../../constants/userConstants";
import { selectFollowGraph } from "../../selectors/userSelectors";

import ProfileCard from "../../components/profile/ProfileCard";
import ProfileActions from "../../components/profile/ProfileActions";
import ProfileStats from "../../components/profile/ProfileStats";
import UserListModal from "../../components/profile/UserListModal";
import PropertyCard from "../../features/realtyList/widgets/PropertyCard";
import ProfileTypeSortFilters from "../../features/realtyList/widgets/ProfileTypeSortFilters";
import { fetchProperties } from "../../features/realtyList/api/realty";
import {
  PROFILE_DEFAULT_DEAL_TYPE,
  PROFILE_DEFAULT_SORT,
  normalizeProfileDealType,
  normalizeProfileSort,
  orderingFromProfileSort,
} from "../../features/realtyList/lib/profilePropertyTypes";
import usePresenceSubscription from "../../features/presence/hooks/usePresenceSubscription";
import { presenceToProfileProps } from "../../features/presence/lib/formatPresence";
import { selectPresenceByUserId } from "../../features/presence/model/presenceSelectors";
import { getPublicProfileHref, getPublicProfileId } from "../../utils/publicProfile";
import {
  listBlocks,
  blockUser as blockUserRequest,
  unblockUser as unblockUserRequest,
} from "./api/userBlocks";
import UserBlockConfirmModal from "./UserBlockConfirmModal";

const FALLBACK_AVATAR = "/images/no-profile-picture.png";
const PUBLIC_CATALOGUE_PAGE_SIZE = 20;
const CLIPBOARD_FAIL_TOAST = "Не вдалося скопіювати посилання.";

function isTargetBlockedInList(blocks, target) {
  if (!target || !Array.isArray(blocks)) return false;
  const targetId = target.id;
  const targetPublicId = target.public_id;
  return blocks.some((entry) => {
    const blocked = entry?.blocked || entry;
    if (!blocked) return false;
    if (targetId != null && blocked.id === targetId) return true;
    if (targetPublicId && blocked.public_id === targetPublicId) return true;
    return false;
  });
}

function isFetchAborted(err) {
  return (
    err?.name === "CanceledError"
    || err?.name === "AbortError"
    || err?.code === "ERR_CANCELED"
  );
}

function readPropertyRows(data) {
  const rows = Array.isArray(data)
    ? data
    : data && Array.isArray(data.results)
      ? data.results
      : [];
  const count = Array.isArray(data)
    ? rows.length
    : (data?.count ?? rows.length);
  return { rows, count };
}

// Collision-proof request id across mounts
let USER_DETAILS_REQ_COUNTER = 0;

const USER_STATS_TILES = [
  {
    id: "realty",
    kind: "link",
    href: "#profile-objects",
    valueKey: "realtyCount",
    labelText: "Нерухомість",
  },
  {
    id: "followers",
    kind: "modal-trigger",
    modalId: "user-followers",
    valueKey: "followers",
    labelText: "Підписники",
  },
  {
    id: "following",
    kind: "modal-trigger",
    modalId: "user-following",
    valueKey: "following",
    labelText: "Підписки",
  },
];

const buildViewedUserSocialLinks = (u) =>
  [
    u?.facebook && {
      network: "facebook",
      url: u.facebook,
      label: "Facebook",
      icon: "fa-facebook-f",
    },
    u?.instagram && {
      network: "instagram",
      url: u.instagram,
      label: "Instagram",
      icon: "fa-instagram",
    },
    u?.youtube && {
      network: "youtube",
      url: u.youtube,
      label: "YouTube",
      icon: "fa-youtube",
    },
  ].filter(Boolean);

const mapViewedPeer = (peer) => ({
  id: peer.public_id || peer.id,
  name: peer.fullname,
  handle: peer.handle ? "@" + peer.handle : "",
  avatar: peer.user_image || FALLBACK_AVATAR,
  href: getPublicProfileHref(peer) || "/users",
});

function readDetailsError(error) {
  if (!error) return { message: null, status: null };
  if (typeof error === "string") return { message: error, status: null };
  return {
    message: error.message || "Не вдалося завантажити профіль",
    status: error.status ?? null,
  };
}

function UserProfileObjects({ ownerId }) {
  const [dealType, setDealType] = useState(PROFILE_DEFAULT_DEAL_TYPE);
  const [typeSet, setTypeSet] = useState(() => new Set());
  const [sort, setSort] = useState(PROFILE_DEFAULT_SORT);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadState, setLoadState] = useState("loading");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [page, setPage] = useState(1);
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);
  const typeKey = Array.from(typeSet).sort().join(",");
  const activeDealType = normalizeProfileDealType(dealType);
  const activeSort = normalizeProfileSort(sort);

  const buildParams = (pageNum, activeTypes, nextSort, nextDealType) => {
    const params = {
      created_by: ownerId,
      page: pageNum,
      page_size: PUBLIC_CATALOGUE_PAGE_SIZE,
      ordering: orderingFromProfileSort(nextSort),
    };
    if (activeTypes.length > 0) {
      params.property_type = [...activeTypes].sort();
    }
    const normalizedDeal = normalizeProfileDealType(nextDealType);
    if (normalizedDeal) {
      params.deal_type = normalizedDeal;
    }
    return params;
  };

  useEffect(() => {
    if (!ownerId) return undefined;
    const requestId = ++requestIdRef.current;
    setLoadState("loading");
    setIsLoadingMore(false);
    setLoadError(null);
    setLoadMoreError(null);
    setResults([]);
    setTotalCount(0);
    setPage(1);
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (_e) {}
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const activeTypes = typeKey ? typeKey.split(",") : [];
    const params = buildParams(1, activeTypes, activeSort, activeDealType);

    fetchProperties(params, { signal: ctrl.signal })
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const { rows, count } = readPropertyRows(data);
        setResults(rows);
        setTotalCount(count);
        setLoadState(rows.length === 0 ? "empty" : "success");
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        if (isFetchAborted(err) || ctrl.signal.aborted) return;
        setLoadError(
          err?.response?.data?.detail || err?.message || "Помилка завантаження",
        );
        setLoadState("error");
      });

    return () => {
      requestIdRef.current += 1;
      // Abort the currently tracked controller (page-1 or a later load-more),
      // not only the page-1 controller closed over by this effect.
      const pending = abortRef.current;
      if (!pending) return;
      try {
        pending.abort();
      } catch (_e) {}
      if (abortRef.current === pending) {
        abortRef.current = null;
      }
    };
    // typeKey is the stable serialization of typeSet; deal/sort/ownerId reset page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildParams closes over ownerId
  }, [ownerId, typeKey, activeSort, activeDealType]);

  const toggleType = (value) => {
    setTypeSet((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const selectDealType = (nextDeal) => {
    setDealType(normalizeProfileDealType(nextDeal));
  };

  const selectSort = (nextSort) => {
    setSort(normalizeProfileSort(nextSort));
  };

  const loadMore = () => {
    if (isLoadingMore || loadState !== "success") return;
    const nextPage = page + 1;
    const rid = ++requestIdRef.current;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (_e) {}
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const params = buildParams(
      nextPage,
      Array.from(typeSet),
      activeSort,
      activeDealType,
    );
    fetchProperties(params, { signal: ctrl.signal })
      .then((data) => {
        if (rid !== requestIdRef.current) return;
        const { rows, count } = readPropertyRows(data);
        setResults((prev) => {
          const seen = new Set(prev.map((row) => row.id));
          return [...prev, ...rows.filter((row) => !seen.has(row.id))];
        });
        setPage(nextPage);
        if (typeof data?.count === "number") {
          setTotalCount(data.count);
        } else if (!Array.isArray(data)) {
          setTotalCount(count);
        } else {
          setTotalCount((prev) => prev + rows.length);
        }
      })
      .catch((err) => {
        if (rid !== requestIdRef.current) return;
        if (isFetchAborted(err) || ctrl.signal.aborted) return;
        setLoadMoreError(
          err?.response?.data?.detail || err?.message || "Помилка завантаження",
        );
      })
      .finally(() => {
        if (rid === requestIdRef.current) {
          setIsLoadingMore(false);
        }
      });
  };

  return (
    <section
      className="profile-objects"
      id="profile-objects"
      aria-label="Об'єкти нерухомості профілю"
    >
      <div
        className="profile-filters"
        role="group"
        aria-label="Фільтри та сортування"
      >
        <ProfileTypeSortFilters
          dealType={dealType}
          onDealTypeChange={selectDealType}
          typeSet={typeSet}
          onToggleType={toggleType}
          sort={sort}
          onSortChange={selectSort}
          sortSelectId="public-profile-sort"
          typeInputName="public-profile-type"
          dealInputName="public-profile-deal-type"
        />
      </div>

      {loadState === "loading" && <Loader />}
      {loadState === "error" && (
        <div className="profile-objects-error" role="alert">
          {loadError || "Не вдалося завантажити об'єкти"}
        </div>
      )}
      {loadState === "empty" && (
        <div className="profile-objects-empty">
          Немає публічних об'єктів нерухомості
        </div>
      )}
      {loadState === "success" && (
        <>
          <p className="profile-objects-count">
            Показано {results.length} з {totalCount} у каталозі
          </p>
          <div className="row">
            {results.map((property) => {
              const owner = property.created_by;
              const ownerHref = getPublicProfileHref(owner);
              return (
                <div key={property.id} className="col-12 col-md-6 col-lg-4 mb-3">
                  <PropertyCard
                    property={property}
                    detailHref={`/realty/${property.id}`}
                    ownerHref={ownerHref || "#"}
                    liked={Boolean(property.liked_by_me)}
                    bookmarked={Boolean(property.bookmarked_by_me)}
                  />
                </div>
              );
            })}
          </div>
          {results.length < totalCount && (
            <div className="text-center my-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={loadMore}
                disabled={isLoadingMore}
              >
                Показати ще
              </button>
              {loadMoreError && (
                <div className="profile-objects-error mt-2" role="alert">
                  {loadMoreError}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function UserScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [showMFollow, setShowMFollow] = useState(false);
  const [showMViewer, setShowMViewer] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const shareCopyTimerRef = useRef(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [blockPending, setBlockPending] = useState(false);
  const blockPendingRef = useRef(false);
  const blocksHydrateReqRef = useRef(0);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const viewedUserId = useSelector((state) => state.userDetails?.user?.id);
  const presence = useSelector((state) =>
    viewedUserId != null ? selectPresenceByUserId(state, viewedUserId) : null
  );
  const presenceSubscribeIds = useMemo(
    () => (userInfo && viewedUserId ? [viewedUserId] : []),
    [userInfo, viewedUserId],
  );
  usePresenceSubscription(presenceSubscribeIds);

  const { following } = useSelector(selectFollowGraph);

  const userDetails = useSelector((state) => state.userDetails);
  const { loading, error, success, user } = userDetails;
  const detailsError = readDetailsError(error);

  const userFollow = useSelector((state) => state.userFollow);
  const {
    loading: loadingFollow,
    error: errorFollow,
    success: successFollow,
  } = userFollow;

  const userUnfollow = useSelector((state) => state.userUnfollow);
  const {
    loading: loadingUnfollow,
    error: errorUnfollow,
    success: successUnfollow,
  } = userUnfollow;

  const chatCreate = useSelector((state) => state.chatCreate);
  const {
    loading: loadingChatCreate,
    error: errorChatCreate,
    createdChat,
  } = chatCreate;

  // Stale-request guard for profile details
  const detailsReqIdRef = useRef(0);
  const detailsAbortRef = useRef(null);
  useEffect(() => {
    dispatch({ type: USER_DETAILS_RESET });
    const rid = ++USER_DETAILS_REQ_COUNTER;
    detailsReqIdRef.current = rid;
    if (detailsAbortRef.current) {
      try {
        detailsAbortRef.current.abort();
      } catch (_e) {}
    }
    const ctrl = new AbortController();
    detailsAbortRef.current = ctrl;
    dispatch(getUserDetails(params.id, { requestId: rid, signal: ctrl.signal }));
    return () => {
      try {
        if (detailsAbortRef.current) {
          detailsAbortRef.current.abort();
        }
      } catch (_e) {}
      dispatch({ type: USER_DETAILS_RESET });
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (userInfo) {
      if (params.id === userInfo.public_id || params.id === userInfo.handle) {
        navigate("/");
      }
    }
    if (successFollow) {
      toast.success("Ви підписали на цього користувача.");
      dispatch({ type: USER_FOLLOW_RESET });
      const rid = ++USER_DETAILS_REQ_COUNTER;
      detailsReqIdRef.current = rid;
      if (detailsAbortRef.current) {
        try {
          detailsAbortRef.current.abort();
        } catch (_e) {}
      }
      const ctrl = new AbortController();
      detailsAbortRef.current = ctrl;
      dispatch(getUserDetails(params.id, { requestId: rid, signal: ctrl.signal }));
    }
    if (errorFollow) {
      toast.error(errorFollow);
      dispatch({ type: USER_FOLLOW_RESET });
    }
    if (successUnfollow) {
      toast.success("Ви відписалися від цього користувача.");
      dispatch({ type: USER_UNFOLLOW_RESET });
      const rid2 = ++USER_DETAILS_REQ_COUNTER;
      detailsReqIdRef.current = rid2;
      if (detailsAbortRef.current) {
        try {
          detailsAbortRef.current.abort();
        } catch (_e) {}
      }
      const ctrl2 = new AbortController();
      detailsAbortRef.current = ctrl2;
      dispatch(getUserDetails(params.id, { requestId: rid2, signal: ctrl2.signal }));
    }
    if (errorUnfollow) {
      toast.error(errorUnfollow);
      dispatch({ type: USER_UNFOLLOW_RESET });
    }
    if (createdChat?.id) {
      toast.success("Чат успішно створено");
      navigate(`/chats/${createdChat.id}`);
      dispatch(resetChatCreate());
    }
    if (errorChatCreate) {
      toast.error(errorChatCreate);
      dispatch(resetChatCreate());
    }
  }, [
    params.id,
    successFollow,
    errorFollow,
    successUnfollow,
    errorUnfollow,
    createdChat,
    errorChatCreate,
    navigate,
    dispatch,
    userInfo,
  ]);

  const handleFollow = () => {
    setShowMFollow(!showMFollow);
  };

  const handleViewer = () => {
    setShowMViewer(!showMViewer);
  };

  const containsObj = (obj, list = []) => {
    return list.some((item) => item.public_id === obj.public_id);
  };

  const followHandler = (target) => {
    dispatch(followUser(target.public_id));
  };

  const unfollowHandler = (target) => {
    dispatch(unfollowUser(target.public_id));
  };

  const createChatHandler = (userId) => {
    dispatch(createChat([userId]));
  };

  // B1-11 — hydrate whether the viewed peer is already blocked.
  useEffect(() => {
    setBlockConfirmOpen(false);
    if (!userInfo || !user?.id) {
      setIsBlocked(false);
      return undefined;
    }
    const rid = ++blocksHydrateReqRef.current;
    const ctrl = new AbortController();
    Promise.resolve(listBlocks({ signal: ctrl.signal }))
      .then((data) => {
        if (rid !== blocksHydrateReqRef.current) return;
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];
        setIsBlocked(isTargetBlockedInList(rows, user));
      })
      .catch((err) => {
        if (rid !== blocksHydrateReqRef.current) return;
        if (isFetchAborted(err) || ctrl.signal.aborted) return;
        setIsBlocked(false);
      });
    return () => {
      blocksHydrateReqRef.current += 1;
      try {
        ctrl.abort();
      } catch (_e) {}
    };
    // Identity-keyed hydrate: re-run when the viewed peer id/public_id changes,
    // not on every profile field patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user object identity via id/public_id
  }, [userInfo, user?.id, user?.public_id]);

  const openBlockConfirm = () => {
    if (blockPendingRef.current) return;
    setBlockConfirmOpen(true);
  };

  const closeBlockConfirm = () => {
    if (blockPendingRef.current) return;
    setBlockConfirmOpen(false);
  };

  const confirmBlock = async () => {
    if (!user?.id || blockPendingRef.current) return;
    blockPendingRef.current = true;
    setBlockPending(true);
    try {
      await blockUserRequest({
        user_id: user.id,
        public_id: user.public_id,
      });
      setIsBlocked(true);
      setBlockConfirmOpen(false);
      toast.success("Користувача заблоковано.");
    } catch (_err) {
      toast.error("Не вдалося заблокувати користувача.");
    } finally {
      blockPendingRef.current = false;
      setBlockPending(false);
    }
  };

  const handleUnblock = async () => {
    if (!user?.id || blockPendingRef.current) return;
    blockPendingRef.current = true;
    setBlockPending(true);
    try {
      await unblockUserRequest(user.id);
      setIsBlocked(false);
      toast.success("Користувача розблоковано.");
    } catch (_err) {
      toast.error("Не вдалося розблокувати користувача.");
    } finally {
      blockPendingRef.current = false;
      setBlockPending(false);
    }
  };

  const handleShareCopy = async () => {
    if (!user) return;
    const handle = getPublicProfileId(user);
    if (!handle) return;
    const url = `${window.location.origin}/user/${handle}`;
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.clipboard ||
        typeof navigator.clipboard.writeText !== "function"
      ) {
        toast.error(CLIPBOARD_FAIL_TOAST);
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      if (shareCopyTimerRef.current) {
        window.clearTimeout(shareCopyTimerRef.current);
      }
      shareCopyTimerRef.current = window.setTimeout(() => {
        setShareCopied(false);
      }, 2000);
    } catch (_err) {
      toast.error(CLIPBOARD_FAIL_TOAST);
    }
  };

  useEffect(() => {
    return () => {
      if (shareCopyTimerRef.current) {
        window.clearTimeout(shareCopyTimerRef.current);
      }
    };
  }, []);

  // Stable MainLayout content shell: loading / error / success all share
  // one top-level #Csection grid item (UI-LAYOUT-CSECTION-A1-R1).
  if (loading || detailsError.message || !success || !user) {
    const isNotFound = detailsError.status === 404;
    return (
      <section className="profile-section main-section" id="Csection">
        {loading ? (
          <Loader />
        ) : (
          <div className="profile-error-state" role="alert">
            <h1>
              {isNotFound ? "Профіль не знайдено" : "Помилка завантаження"}
            </h1>
            <p>
              {isNotFound
                ? "Користувача з таким ідентифікатором не існує або він недоступний."
                : detailsError.message ||
                  "Не вдалося завантажити профіль. Спробуйте пізніше."}
            </p>
            <Link to="/users" className="btn btn-primary">
              До списку користувачів
            </Link>
          </div>
        )}
      </section>
    );
  }

  const isFollowing = containsObj(user, following);
  const followBusy = loadingFollow || loadingUnfollow;
  const ownerPublicId = getPublicProfileId(user);

  const presenceProps = userInfo
    ? presenceToProfileProps(presence)
    : { isOnline: null, lastSeen: null };

  const profileUserShape = {
    name: user.fullname || "",
    avatar: user.user_image || FALLBACK_AVATAR,
    avatarAlt: user.fullname ? "Аватар " + user.fullname : "Аватар",
    bio: user.description || "",
    isOnline: presenceProps.isOnline,
    lastSeen: presenceProps.lastSeen,
  };

  const viewerMenuItems = [
    {
      id: "share",
      label: shareCopied ? "Посилання скопійовано" : "Поширити",
      icon: "fa-share-nodes",
      kind: "button",
      onClick: handleShareCopy,
    },
    {
      id: "block",
      label: isBlocked ? "Розблокувати" : "Заблокувати",
      icon: isBlocked ? "fa-lock-open" : "fa-ban",
      kind: "button",
      danger: !isBlocked,
      disabled: blockPending,
      onClick: isBlocked ? handleUnblock : openBlockConfirm,
    },
  ];

  const viewerPrimaryActions = [
    {
      id: "follow",
      label: isFollowing ? "Відписатися" : "Підписатися",
      icon: isFollowing ? "fa-user-minus" : "fa-user-plus",
      variant: "filled",
      loading: followBusy,
      onClick: () =>
        isFollowing ? unfollowHandler(user) : followHandler(user),
    },
    {
      id: "message",
      label: "Повідомлення",
      icon: "fa-paper-plane",
      loading: loadingChatCreate,
      onClick: () => createChatHandler(user.id),
    },
  ];

  const viewerSecondaryActions = [
    ...(user.phone_number
      ? [
          {
            id: "phone",
            label: "Телефон",
            icon: "fa-phone",
            href: "tel:" + user.phone_number,
          },
        ]
      : []),
    ...(user.email
      ? [
          {
            id: "email",
            label: "Емейл",
            icon: "fa-envelope",
            href: "mailto:" + user.email,
          },
        ]
      : []),
  ];

  const statsValues = {
    realtyCount: user.realty_count || 0,
    followers: user.followers_count || 0,
    following: user.following_count || 0,
  };

  return (
    <section className="profile-section main-section" id="Csection">
      <section
        className="profile-info-section"
        aria-label="Інформація про профіль"
      >
        <ProfileCard
          user={profileUserShape}
          company={null}
          socialLinks={buildViewedUserSocialLinks(user)}
          menuItems={viewerMenuItems}
        />
        <ProfileActions
          primaryActions={viewerPrimaryActions}
          secondaryActions={viewerSecondaryActions}
        />
        <ProfileStats
          tiles={USER_STATS_TILES}
          stats={statsValues}
          onOpenFollowers={handleViewer}
          onOpenFollowing={handleFollow}
        />
      </section>
      <UserProfileObjects
        key={ownerPublicId}
        ownerId={ownerPublicId}
      />
      <UserListModal
        id="user-following"
        title="Підписки"
        ariaLabel="Список підписок"
        users={(user.following || []).map(mapViewedPeer)}
        isOpen={showMFollow}
        onClose={handleFollow}
      />
      <UserListModal
        id="user-followers"
        title="Підписники"
        ariaLabel="Список підписників"
        users={(user.followers || []).map(mapViewedPeer)}
        isOpen={showMViewer}
        onClose={handleViewer}
      />
      <UserBlockConfirmModal
        open={blockConfirmOpen}
        userName={user.fullname}
        pending={blockPending}
        onCancel={closeBlockConfirm}
        onConfirm={confirmBlock}
      />
    </section>
  );
}

export default UserScreen;
